import { handleComplete, handleErrorOrCancelled, handleProgress, isJobCancelled, resetPhase } from './job_state.js';
import { JOB_PHASES } from './config/jobPhases.js';
import { JOB_MONITORING } from './config/jobMonitoring.js';
import * as API from './api.js';

let eventSource = null;
let fallbackTimer = null;
let staleTimer = null;
let fallbackActive = false;
let lastEventTs = 0;
let activeJobId = null;
let reconnectTimer = null;
let reconnectDelayMs = 1000;
let fallbackIntervalMs = null;
const RECONNECT_MAX_MS = 10000;

function _getMonitoringTimings() {
    const staleS = Number(JOB_MONITORING.SSE_STALE_AFTER_S || 8);
    const pollS = Number(JOB_MONITORING.FALLBACK_POLL_S || 3);
    return {
        staleMs: Math.max(1000, staleS * 1000),
        pollMs: Math.max(1000, pollS * 1000)
    };
}

function _clearFallbackTimers() {
    if (fallbackTimer) {
        clearInterval(fallbackTimer);
        fallbackTimer = null;
    }
    if (staleTimer) {
        clearInterval(staleTimer);
        staleTimer = null;
    }
}

function _clearReconnectTimer() {
    if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
    }
}

function _resetReconnectDelay() {
    reconnectDelayMs = 1000;
}

function _scheduleReconnect(jobId, suppressBuildToast) {
    if (!jobId || reconnectTimer) return;
    const delay = reconnectDelayMs;
    reconnectDelayMs = Math.min(reconnectDelayMs * 2, RECONNECT_MAX_MS);
    reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        if (!activeJobId || activeJobId !== jobId) return;
        _connectEventSource(jobId, suppressBuildToast);
    }, delay);
}

function _stopFallback(options = {}) {
    const { logRecovery = false, reason = 'sse_message' } = options;
    if (fallbackActive && logRecovery && activeJobId) {
        API.logEvent('job_status_recovered', { job_id: activeJobId, reason }).catch(() => {});
    }
    fallbackActive = false;
    fallbackIntervalMs = null;
    if (fallbackTimer) {
        clearInterval(fallbackTimer);
        fallbackTimer = null;
    }
}

function _connectEventSource(jobId, suppressBuildToast) {
    if (!jobId) return;
    if (eventSource) {
        eventSource.close();
        eventSource = null;
    }
    console.log(`📡 [SSE] Connecting to stream for job ${jobId}...`);
    eventSource = new EventSource(`/events/${jobId}`);

    eventSource.onmessage = async (event) => {
        if (isJobCancelled()) {
            stopMonitoring();
            return;
        }

        try {
            const data = JSON.parse(event.data);
            lastEventTs = Date.now();
            if (fallbackActive) {
                _stopFallback({ logRecovery: true, reason: 'sse_message' });
            }
            _resetReconnectDelay();
            _clearReconnectTimer();

            if (data.phase === JOB_PHASES.COMPLETE) {
                stopMonitoring();
                await handleComplete(jobId);
                return;
            }

            if (data.phase === JOB_PHASES.ERROR || data.phase === JOB_PHASES.CANCELLED) {
                stopMonitoring();
                handleErrorOrCancelled(jobId, data);
                return;
            }

            handleProgress(jobId, data, { suppressBuildToast });
        } catch (e) {
            console.error("SSE Parse Error:", e);
        }
    };

    eventSource.onerror = () => {
        console.log("📡 [SSE] Connection lost (or closed).");
        if (!fallbackActive) {
            lastEventTs = 0;
        }
        if (!activeJobId || activeJobId !== jobId) return;
        if (eventSource) {
            eventSource.close();
            eventSource = null;
        }
        if (!fallbackActive) {
            _startFallback(jobId, suppressBuildToast, 'sse_error');
        }
        _scheduleReconnect(jobId, suppressBuildToast);
    };
}

async function _pollStatus(jobId, suppressBuildToast) {
    if (!jobId || activeJobId !== jobId || isJobCancelled()) return;
    try {
        const data = await API.getStatus(jobId);
        if (!data || !data.phase) return;
        if (data.phase === JOB_PHASES.COMPLETE) {
            stopMonitoring();
            await handleComplete(jobId);
            return;
        }
        if (data.phase === JOB_PHASES.ERROR || data.phase === JOB_PHASES.CANCELLED) {
            stopMonitoring();
            handleErrorOrCancelled(jobId, data);
            return;
        }
        handleProgress(jobId, data, { suppressBuildToast });
    } catch (e) {
        if (e && e.retryAfter) {
            const timings = _getMonitoringTimings();
            const retryMs = Math.max(timings.pollMs, Number(e.retryAfter) * 1000);
            if (fallbackActive && retryMs && retryMs !== fallbackIntervalMs) {
                _setFallbackInterval(jobId, suppressBuildToast, retryMs);
            }
            API.logEvent('job_status_backoff', { job_id: jobId, retry_after_s: e.retryAfter }).catch(() => {});
        }
        console.warn('Status poll failed:', e);
    }
}

function _setFallbackInterval(jobId, suppressBuildToast, intervalMs) {
    if (!fallbackActive || !jobId) return;
    if (fallbackTimer) {
        clearInterval(fallbackTimer);
        fallbackTimer = null;
    }
    fallbackIntervalMs = intervalMs;
    fallbackTimer = setInterval(() => {
        _pollStatus(jobId, suppressBuildToast);
    }, intervalMs);
}

function _startFallback(jobId, suppressBuildToast, reason = 'sse_stale') {
    if (fallbackActive || !jobId) return;
    fallbackActive = true;
    API.logEvent('job_status_fallback', { job_id: jobId, reason }).catch(() => {});
    _pollStatus(jobId, suppressBuildToast);
    const timings = _getMonitoringTimings();
    _setFallbackInterval(jobId, suppressBuildToast, timings.pollMs);
}

export function stopMonitoring() {
    if (eventSource) {
        console.log("📡 [SSE] Closing connection.");
        eventSource.close();
        eventSource = null;
    }
    _clearFallbackTimers();
    _stopFallback({ logRecovery: false });
    _clearReconnectTimer();
    _resetReconnectDelay();
    activeJobId = null;
    lastEventTs = 0;
}

export function startMonitoringJob(jobId, options = {}) {
    stopMonitoring();
    resetPhase();
    const suppressBuildToast = Boolean(options && options.suppressBuildToast);
    activeJobId = jobId;
    lastEventTs = Date.now();

    _connectEventSource(jobId, suppressBuildToast);

    const timings = _getMonitoringTimings();
    staleTimer = setInterval(() => {
        if (!eventSource || fallbackActive || !activeJobId) return;
        const idleMs = Date.now() - (lastEventTs || 0);
        if (idleMs >= timings.staleMs) {
            _startFallback(activeJobId, suppressBuildToast, 'sse_stale');
        }
    }, 1000);
}
